import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { useStoreSettings } from './StoreSettingsContext';
import { PinUnlockModal } from '../components/ui/PinUnlockModal';
import { showToast } from '../lib/toast';

// Chave do localStorage para persistir a preferência
const STORAGE_KEY = 'optisaas_values_visible';

interface PrivacyContextType {
  /** Indica se os valores monetários estão visíveis */
  valuesVisible: boolean;
  /** Permissão para editar ou excluir (true apenas quando os valores estiverem visíveis/desbloqueados) */
  canEditOrDelete: boolean;
  /** Indica se o modo restrito (valores ocultos) está ativo */
  isRestrictedMode: boolean;
  /** Alterna entre visível/oculto (se estiver oculto, solicita PIN) */
  toggleValuesVisibility: () => void;
  /** Valida o PIN de 4 dígitos e desbloqueia */
  unlockWithPin: (pin: string) => boolean;
  /** Abre o modal de PIN manualmente */
  openPinModal: () => void;
  /** Fecha o modal de PIN */
  closePinModal: () => void;
  /** Helper que verifica se pode editar/apagar; se não puder, abre o PIN modal e avisa */
  requireUnlock: (actionDescription?: string) => boolean;
}

const PrivacyContext = createContext<PrivacyContextType | undefined>(undefined);

/**
 * Provider que gerencia o estado global de visibilidade dos valores monetários
 * e as permissões de edição/exclusão protegidas pelo PIN da nuvem.
 */
export const PrivacyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { storeSettings } = useStoreSettings();
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);

  const [valuesVisible, setValuesVisible] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      // Se não houver valor salvo, padrão é visível (true)
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  // Persistir mudanças no localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(valuesVisible));
    } catch {
      // Falha silenciosa se localStorage não estiver disponível
    }
  }, [valuesVisible]);

  const openPinModal = useCallback(() => {
    setIsPinModalOpen(true);
  }, []);

  const closePinModal = useCallback(() => {
    setIsPinModalOpen(false);
  }, []);

  // Alterna: se estiver visível, oculta na hora. Se estiver oculto, pede o PIN.
  const toggleValuesVisibility = useCallback(() => {
    if (valuesVisible) {
      setValuesVisible(false);
      showToast('Modo Valores Ocultos ativado. Edição e exclusão bloqueadas.', 'info');
    } else {
      setIsPinModalOpen(true);
    }
  }, [valuesVisible]);

  // Validação do PIN com o valor persistido na nuvem/StoreSettings
  const unlockWithPin = useCallback((pin: string): boolean => {
    const expectedPin = storeSettings.privacyPin?.trim() || '1234';
    if (pin.trim() === expectedPin) {
      setValuesVisible(true);
      setIsPinModalOpen(false);
      showToast('Modo Valores Ocultos desativado. Valores e permissões liberados!', 'success');
      return true;
    }
    return false;
  }, [storeSettings.privacyPin]);

  // Interceptor para botões de edição ou exclusão
  const requireUnlock = useCallback((actionDescription?: string): boolean => {
    if (valuesVisible) return true;
    setIsPinModalOpen(true);
    showToast(
      `🔒 Modo Restrito Ativo. Digite o PIN para ${actionDescription || 'editar ou apagar'}.`,
      'warning'
    );
    return false;
  }, [valuesVisible]);

  return (
    <PrivacyContext.Provider
      value={{
        valuesVisible,
        canEditOrDelete: valuesVisible,
        isRestrictedMode: !valuesVisible,
        toggleValuesVisibility,
        unlockWithPin,
        openPinModal,
        closePinModal,
        requireUnlock,
      }}
    >
      {children}
      <PinUnlockModal
        isOpen={isPinModalOpen}
        onClose={closePinModal}
        onSuccess={unlockWithPin}
      />
    </PrivacyContext.Provider>
  );
};

/**
 * Hook para acessar o estado de privacidade e travas de segurança.
 */
export const usePrivacy = (): PrivacyContextType => {
  const context = useContext(PrivacyContext);
  if (context === undefined) {
    throw new Error('usePrivacy deve ser usado dentro de um PrivacyProvider');
  }
  return context;
};
